import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import { useShallowEqualSelector } from '@/hooks';
import { useHasAccess } from '@/hooks/user';
import { VIEWS_NAMES } from '@/pages/Collaborators/CollaboratosModal/constants';
import { capitalize } from '@/utils';

import * as Forms from './Forms/Forms';

const tabs = [
	{
		name: 'generalInfo',
		sections: [
			'contact',
			'password',
			'language',
			'timezone',
			'dateTime',
			'closeAccount'
		]
	},
	{
		name: VIEWS_NAMES.TAX_INFO,
		sections: ['taxInfo']
	}
];

dayjs.extend(localizedFormat);

function Account() {
	const { t } = useTranslation();
	const { currentTab, updatedAt } = useShallowEqualSelector(({ profile }) => ({
		currentTab: profile.currentTab,
		updatedAt: profile.user.updatedAt
	}));
	const hasAccess = useHasAccess();
	const canUserSeeTaxInfo = hasAccess(VIEWS_NAMES.TAX_INFO);
	const filteredTabs = tabs.filter(
		({ name }) => canUserSeeTaxInfo || name !== VIEWS_NAMES.TAX_INFO
	);

	return (
		<div className="px-5 md:px-8">
			<h1 className="mt-[56px] font-title text-[32px] font-semibold leading-10">
				{t('pageTitles.myAccount')}
			</h1>
			<div className="mt-10 flex w-full">
				<Tabs className="w-full" defaultValue={currentTab || 'generalInfo'}>
					<TabsList className="mb-3 flex justify-start gap-4 lg:mb-4">
						{filteredTabs.map(({ name }) => {
							return (
								<TabsTrigger
									className="rounded data-[state=active]:bg-red-background"
									value={name}
									key={name}
								>
									{t(`account.tabs.${name}`)}
								</TabsTrigger>
							);
						})}
					</TabsList>

					<div className="mb-2 rounded bg-grey-100 px-6 py-1 text-center text-2xs text-grey-500 md:text-left md:text-xs md:leading-5">
						{t('common.updatedOn', {
							date: dayjs.unix(updatedAt).format('LL')
						})}
					</div>

					{filteredTabs.map(({ name, sections }) => {
						return (
							<TabsContent
								value={name}
								className="mx-0 mb-6 flex max-w-full flex-col gap-4 lg:mb-[100px] lg:gap-6"
								key={name}
							>
								{sections.map(section => {
									const CurrentSection = Forms[capitalize(section)];
									return (
										<CurrentSection
											key={section}
											title={t(`account.sections.${section}.title`)}
											description={t(
												`account.sections.${section}.description`
											)}
										/>
									);
								})}
							</TabsContent>
						);
					})}
				</Tabs>
			</div>
		</div>
	);
}

export default Account;
